import { useEffect, useMemo, useRef, useState } from "react";
import { BUTTON_HIDE_EXIT_MS } from "./ButtonHideCountdownFrame";

function afterSecondsButtonsDependencyKey(playElements) {
	return (playElements || [])
		.filter((e) => e?.type === "button" && String(e?.content?.showWhen || "") === "afterSeconds")
		.map((e) => `${String(e.elementId)}:${Number(e.content?.showAfterSeconds) || 0}`)
		.sort()
		.join("|");
}

function hideAfterButtonsDependencyKey(playElements) {
	return (playElements || [])
		.filter((e) => e?.type === "button" && Math.max(0, Number(e?.content?.hideAfterSeconds) || 0) > 0)
		.map((e) => `${String(e.elementId)}:${Number(e.content?.hideAfterSeconds) || 0}`)
		.sort()
		.join("|");
}

/** IDs de botones con showWhen=afterSeconds que ya pueden mostrarse en la página actual. */
export function useAfterSecondsButtonReady(playPageIndex, playElements) {
	const [readyIds, setReadyIds] = useState(() => new Set());
	const depKey = useMemo(() => afterSecondsButtonsDependencyKey(playElements), [playElements]);

	useEffect(() => {
		const buttons = (playElements || []).filter(
			(e) => e?.type === "button" && String(e?.content?.showWhen || "") === "afterSeconds"
		);
		setReadyIds(new Set());
		if (!buttons.length) return undefined;

		const pageEnterMs = Date.now();
		const deadlines = new Map();
		for (const btn of buttons) {
			const sec = Math.max(0, Number(btn.content?.showAfterSeconds) || 0);
			deadlines.set(String(btn.elementId), pageEnterMs + sec * 1000);
		}

		const flush = () => {
			const now = Date.now();
			setReadyIds((prev) => {
				const next = new Set(prev);
				let changed = false;
				deadlines.forEach((deadline, id) => {
					if (now >= deadline && !next.has(id)) {
						next.add(id);
						changed = true;
					}
				});
				return changed ? next : prev;
			});
		};

		flush();
		const intervalId = window.setInterval(flush, 280);
		const onVis = () => {
			if (document.visibilityState === "visible") flush();
		};
		document.addEventListener("visibilitychange", onVis);
		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener("visibilitychange", onVis);
		};
	}, [playPageIndex, depKey, playElements]);

	return readyIds;
}

/**
 * Botones con hideAfterSeconds > 0: al pasar a visibles (showWhen OK),
 * se marcan caducados tras esa duración desde el momento en que aparecieron.
 */
export function useHideAfterSecondsExpired(playPageIndex, playElements, baseVisibleIds) {
	const [expiredIds, setExpiredIds] = useState(() => new Set());
	const shownAtRef = useRef(new Map());
	const depKey = useMemo(() => hideAfterButtonsDependencyKey(playElements), [playElements]);
	const visibleKey = useMemo(() => {
		const arr = baseVisibleIds instanceof Set ? [...baseVisibleIds] : [...(baseVisibleIds || [])];
		return arr.map(String).sort().join("|");
	}, [baseVisibleIds]);

	useEffect(() => {
		setExpiredIds(new Set());
		shownAtRef.current = new Map();
	}, [playPageIndex, depKey]);

	useEffect(() => {
		const buttons = (playElements || []).filter(
			(e) => e?.type === "button" && Math.max(0, Number(e?.content?.hideAfterSeconds) || 0) > 0
		);
		if (!buttons.length) {
			setExpiredIds(new Set());
			return undefined;
		}

		const visibleSet =
			baseVisibleIds instanceof Set
				? baseVisibleIds
				: new Set([...(baseVisibleIds || [])].map(String));

		const now = Date.now();
		for (const btn of buttons) {
			const id = String(btn.elementId);
			if (visibleSet.has(id) && !shownAtRef.current.has(id)) {
				shownAtRef.current.set(id, now);
			}
		}

		const flush = () => {
			const t = Date.now();
			setExpiredIds((prev) => {
				const next = new Set(prev);
				let changed = false;
				for (const btn of buttons) {
					const id = String(btn.elementId);
					if (next.has(id)) continue;
					const shownAt = shownAtRef.current.get(id);
					if (shownAt == null) continue;
					const sec = Math.max(0, Number(btn.content?.hideAfterSeconds) || 0);
					if (sec > 0 && t >= shownAt + sec * 1000 + BUTTON_HIDE_EXIT_MS) {
						next.add(id);
						changed = true;
					}
				}
				return changed ? next : prev;
			});
		};

		flush();
		const intervalId = window.setInterval(flush, 280);
		const onVis = () => {
			if (document.visibilityState === "visible") flush();
		};
		document.addEventListener("visibilitychange", onVis);
		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener("visibilitychange", onVis);
		};
	}, [playPageIndex, depKey, visibleKey, playElements, baseVisibleIds]);

	return expiredIds;
}

export function isButtonHiddenByShowWhen(el, ctx) {
	if (el?.type !== "button") return false;
	const sw = el.content?.showWhen || "always";
	if (sw === "quizCompleted" && !ctx.allQuizzesOnPageCompleted) return true;
	if (sw === "allButtonsActivated") {
		const others = ctx.pageButtons.filter((b) => b.elementId !== el.elementId);
		if (others.length > 0 && !others.every((b) => ctx.activatedSet.has(b.elementId))) return true;
	}
	if (sw === "afterSeconds" && !ctx.afterSecondsReady.has(String(el.elementId))) return true;
	return false;
}

export function isButtonHidden(el, ctx) {
	if (isButtonHiddenByShowWhen(el, ctx)) return true;
	if (ctx.hideAfterExpired?.has(String(el.elementId))) return true;
	return false;
}
